import translate from '@/utils/translate';

/**
 * Maps every deliverableKind value to its locale key (key === value convention).
 */
const DELIVERABLE_KIND_LOCALE_KEY: Record<string, string> = {
  amazon_post: 'Amazon Post',
  blog_post: 'Blog Post',
  facebook_post: 'Post',
  instagram_post: 'Post',
  instagram_reel_post: 'Reel',
  instagram_story_post: 'Story',
  linkedin_post: 'LinkedIn Post',
  pinterest_post: 'Pinterest Post',
  reddit_post: 'Reddit Post',
  review_post: 'Review',
  snapchat_post: 'Snapchat Post',
  tiktok_post: 'TikTok Post',
  twitch_post: 'Twitch Post',
  twitter_post: 'X (Twitter) Post',
  youtube_post: 'YouTube Post',
};

/**
 * Maps kind values to locale keys for content campaigns (no network).
 * Note: mixed_media → 'Mixed media' here (card label context).
 * In breadcrumbUtils.ts, mixed_media → 'Story' (breadcrumb navigation context).
 */
const KIND_LOCALE_KEY: Record<string, string> = {
  blog: 'Blog',
  mixed_media: 'Mixed media',
  photo: 'Photo',
  video: 'Video',
};

const NETWORK_TO_DELIVERABLE_KIND: Record<string, string> = {
  amazon: 'amazon_post',
  facebook: 'facebook_post',
  linkedin: 'linkedin_post',
  pinterest: 'pinterest_post',
  pinterest_organic: 'pinterest_post',
  reddit: 'reddit_post',
  snapchat: 'snapchat_post',
  tiktok: 'tiktok_post',
  tiktok_v2: 'tiktok_post',
  tiktok_creator_marketplace: 'tiktok_post',
  twitch: 'twitch_post',
  twitter: 'twitter_post',
  youtube: 'youtube_post',
};

/**
 * Deduces the most specific deliverableKind from campaign network + content kind.
 * Instagram is the only network where the content kind changes the deliverable type.
 */
function deduceDeliverableKind(
  campaignNetwork?: string | null,
  kind?: string | null
): string | null {
  if (!campaignNetwork) {
    return null;
  }

  if (campaignNetwork.includes('instagram')) {
    if (kind === 'video') {
      return 'instagram_reel_post';
    }
    if (kind === 'mixed_media') {
      return 'instagram_story_post';
    }
    return 'instagram_post';
  }

  return NETWORK_TO_DELIVERABLE_KIND[campaignNetwork] ?? null;
}

/**
 * Returns the translated content type label.
 * Priority: deliverableKind → deduce from network+kind → kind fallback.
 */
function getContentTypeLabel(
  deliverableKind?: string | null,
  kind?: string | null,
  campaignNetwork?: string | null
): string {
  const resolvedKind =
    deliverableKind ?? deduceDeliverableKind(campaignNetwork, kind);

  const localeKey = resolvedKind
    ? DELIVERABLE_KIND_LOCALE_KEY[resolvedKind]
    : null;

  if (localeKey) return translate(localeKey);

  // Content campaign (no network) — prettify the raw kind value.
  const kindLocaleKey = kind ? KIND_LOCALE_KEY[kind] : null;
  if (kindLocaleKey) {
    return translate(kindLocaleKey);
  }

  return kind ?? '';
}

export default getContentTypeLabel;
